/**
 * Hobbies Stuff — Artwork Pricing Embed
 * ======================================
 * Fetches pricing config from the pricing app, injects a full artwork-options
 * UI into the Shopify product page, and dynamically updates the displayed price.
 *
 * Installation (in layout/theme.liquid, before </body>):
 *
 *   <script>
 *     window.HSPricingConfig = {
 *       appUrl: 'https://your-pricing-app.onrender.com'
 *     };
 *   </script>
 *   <script src="{{ 'artwork-embed.js' | asset_url }}" defer></script>
 */
(function () {
  'use strict';

  // ── Config ─────────────────────────────────────────────────────────────────
  var cfg      = window.HSPricingConfig || {};
  var APP_URL  = (cfg.appUrl || '').replace(/\/$/, '');
  var CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  var CACHE_KEY = 'hs_pricing_cfg';

  if (!APP_URL) {
    console.warn('[HS Pricing] window.HSPricingConfig.appUrl is not set.');
    return;
  }

  // Only run on product pages
  if (!document.querySelector('#pdp-display-price')) return;

  // ── State ──────────────────────────────────────────────────────────────────
  var pricingConfig = null;  // full config from API
  var selections    = {};    // { category_key: option_value }
  var checkedAddons = {};    // { addon_id: true/false }
  var baseVariantPrice = 0;

  // ── Cache helpers ──────────────────────────────────────────────────────────
  function getCached() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (Date.now() - obj.ts > CACHE_TTL) return null;
      return obj.data;
    } catch (e) { return null; }
  }

  function setCache(data) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data })); } catch (e) {}
  }

  // ── Price calculation (client-side) ───────────────────────────────────────
  function calcTotal() {
    if (!pricingConfig) return baseVariantPrice;
    // Use product's own Shopify price as base (set via HSPricingConfig.basePrice)
    // Falls back to app base_price setting if not provided
    var total = (cfg.basePrice && cfg.basePrice > 0) ? cfg.basePrice : (pricingConfig.base_price || baseVariantPrice);

    // Add option adjustments
    pricingConfig.categories.forEach(function (cat) {
      var val = selections[cat.key];
      if (!val) return;
      var opt = cat.options.find(function (o) { return o.value === val; });
      if (opt) total += opt.price_adj;
    });

    // Add checked add-ons
    pricingConfig.addons.forEach(function (addon) {
      if (checkedAddons[addon.id]) total += addon.price;
    });

    return Math.max(0, total);
  }

  function buildBreakdown() {
    var sym = pricingConfig ? pricingConfig.currency_symbol : '$';
    var lines = [];
    var baseAmt = (cfg.basePrice && cfg.basePrice > 0) ? cfg.basePrice : pricingConfig.base_price;
    if (pricingConfig) lines.push({ label: 'Base', amount: baseAmt });

    pricingConfig && pricingConfig.categories.forEach(function (cat) {
      var val = selections[cat.key];
      if (!val) return;
      var opt = cat.options.find(function (o) { return o.value === val; });
      if (opt && opt.price_adj !== 0) {
        lines.push({ label: cat.label + ': ' + val, amount: opt.price_adj });
      }
    });

    pricingConfig && pricingConfig.addons.forEach(function (addon) {
      if (checkedAddons[addon.id] && addon.price > 0)
        lines.push({ label: addon.label, amount: addon.price });
    });

    return { sym: sym, lines: lines };
  }

  // ── DOM update ─────────────────────────────────────────────────────────────
  function updatePriceDisplay() {
    var total     = calcTotal();
    var sym       = pricingConfig ? pricingConfig.currency_symbol : '$';
    var formatted = sym + total.toFixed(2);

    var priceEl = document.getElementById('pdp-display-price');
    if (priceEl) priceEl.textContent = formatted;

    // Update breakdown tooltip
    var breakdown = buildBreakdown();
    var bdEl = document.getElementById('hs-price-breakdown');
    if (bdEl && breakdown.lines.length > 1) {
      bdEl.innerHTML = breakdown.lines.map(function (l) {
        var sign = l.amount >= 0 ? '+' : '';
        return '<span class="hs-bd-row"><span>' + escHtml(l.label) + '</span>'
          + '<span>' + (l.label === 'Base' ? '' : sign) + breakdown.sym + Math.abs(l.amount).toFixed(2) + '</span></span>';
      }).join('');
      bdEl.style.display = 'block';
    } else if (bdEl) {
      bdEl.style.display = 'none';
    }
  }

  // ── Option selection handler ───────────────────────────────────────────────
  function selectOption(catKey, value, btnEl) {
    selections[catKey] = value;
    // Highlight button in group
    var group = btnEl.closest('[data-hs-cat="' + catKey + '"]');
    if (group) {
      group.querySelectorAll('.hs-opt-btn').forEach(function (b) { b.classList.remove('active'); });
    }
    btnEl.classList.add('active');
    updatePriceDisplay();
  }

  function toggleAddon(addonId, checked) {
    checkedAddons[addonId] = checked;
    updatePriceDisplay();
  }

  // ── UI rendering ──────────────────────────────────────────────────────────
  function renderCategories(categories) {
    return categories.map(function (cat) {
      if (!cat.options || !cat.options.length) return '';

      // Default to first option
      if (!selections[cat.key]) selections[cat.key] = cat.options[0].value;

      var buttons = cat.options.map(function (opt, i) {
        var isActive = (opt.value === selections[cat.key]) ? ' active' : '';
        var adjLabel = opt.price_adj > 0  ? ' <span class="hs-adj hs-adj--plus">+$' + opt.price_adj.toFixed(0) + '</span>'
                     : opt.price_adj < 0  ? ' <span class="hs-adj hs-adj--minus">-$' + Math.abs(opt.price_adj).toFixed(0) + '</span>'
                     : '';
        return '<button type="button" class="pdp-opt-btn hs-opt-btn' + isActive + '"'
          + ' data-cat="' + escHtml(cat.key) + '" data-val="' + escHtml(opt.value) + '">'
          + escHtml(opt.value) + adjLabel + '</button>';
      }).join('');

      var sym = pricingConfig ? pricingConfig.currency_symbol : '$';
      var selAdj = (function () {
        var o = cat.options.find(function (o) { return o.value === selections[cat.key]; });
        return o && o.price_adj !== 0
          ? '<span class="pdp-option-selected"> — ' + escHtml(o.value) + (o.price_adj > 0 ? ' (+' + sym + o.price_adj.toFixed(0) + ')' : ' (-' + sym + Math.abs(o.price_adj).toFixed(0) + ')') + '</span>'
          : '<span class="pdp-option-selected"> — ' + escHtml(selections[cat.key]) + '</span>';
      }());

      return '<div class="pdp-option-group" data-hs-cat="' + escHtml(cat.key) + '">'
        + '<label class="pdp-option-label">' + escHtml(cat.label) + selAdj + '</label>'
        + (cat.help_text ? '<p class="hs-help">' + escHtml(cat.help_text) + '</p>' : '')
        + '<div class="hs-btn-row">' + buttons + '</div>'
        + '</div>';
    }).join('');
  }

  function renderAddons(addons) {
    if (!addons || !addons.length) return '';
    var sym = pricingConfig.currency_symbol;
    var items = addons.map(function (addon) {
      var priceLabel = addon.price > 0 ? ' +' + sym + addon.price.toFixed(0) : 'Free';
      return '<label class="hs-addon">'
        + '<input type="checkbox" class="hs-addon-cb" data-addon-id="' + addon.id + '" name="properties[Addon: ' + escHtml(addon.label) + ']" value="Yes">'
        + '<div class="hs-addon-inner">'
        + (addon.icon ? '<span class="hs-addon-icon">' + addon.icon + '</span>' : '')
        + '<div><strong>' + escHtml(addon.label) + '</strong>'
        + '<span class="hs-addon-price">' + priceLabel + '</span></div>'
        + '</div></label>';
    }).join('');
    return '<div class="pdp-option-group">'
      + '<label class="pdp-option-label">Add-ons <span style="font-weight:400;text-transform:none;letter-spacing:0;font-size:0.72rem;color:var(--muted)">(Optional)</span></label>'
      + '<div class="hs-addon-grid">' + items + '</div>'
      + '</div>';
  }

  function renderPriceBreakdown() {
    return '<div id="hs-price-breakdown" class="hs-breakdown" style="display:none"></div>';
  }

  function injectHiddenProperties() {
    var form = document.querySelector('form[action="/cart/add"]');
    if (!form) return;

    pricingConfig.categories.forEach(function (cat) {
      var val = selections[cat.key];
      if (!val) return;
      var inp = form.querySelector('input[name="properties[' + cat.label + ']"]');
      if (!inp) {
        inp = document.createElement('input');
        inp.type = 'hidden';
        inp.name = 'properties[' + cat.label + ']';
        inp.id   = 'hs-prop-' + cat.key;
        form.appendChild(inp);
      }
      inp.value = val;
    });

    // Calculated price property
    var calcEl = form.querySelector('input[name="properties[_Calculated Price]"]');
    if (!calcEl) {
      calcEl = document.createElement('input');
      calcEl.type = 'hidden';
      calcEl.name = 'properties[_Calculated Price]';
      calcEl.id   = 'hs-prop-calc-price';
      form.appendChild(calcEl);
    }
    calcEl.value = pricingConfig.currency_symbol + calcTotal().toFixed(2);
  }

  // ── Inject CSS ────────────────────────────────────────────────────────────
  function injectCSS() {
    if (document.getElementById('hs-pricing-css')) return;
    var style = document.createElement('style');
    style.id = 'hs-pricing-css';
    style.textContent = [
      '.hs-btn-row{display:flex;flex-wrap:wrap;gap:8px}',
      '.hs-opt-btn{position:relative}',
      '.hs-adj{font-size:0.62rem;font-weight:700;margin-left:3px;letter-spacing:0;text-transform:none}',
      '.hs-adj--plus{color:var(--rust,#b8440a)}',
      '.hs-adj--minus{color:var(--sage,#4a6741)}',
      '.hs-help{font-size:0.7rem;color:var(--muted,#7a6a52);margin:-4px 0 6px;line-height:1.4}',
      '.hs-addon-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:8px}',
      '.hs-addon{border:1.5px solid rgba(200,146,42,.22);border-radius:4px;cursor:pointer;display:block;transition:border-color .15s,background .15s}',
      '.hs-addon input[type=checkbox]{display:none}',
      '.hs-addon-inner{padding:11px 14px;display:flex;align-items:center;gap:12px}',
      '.hs-addon-icon{font-size:20px;line-height:1}',
      '.hs-addon strong{display:block;font-size:12.5px;color:var(--ink,#1a1208)}',
      '.hs-addon-price{font-size:11px;font-weight:700;color:var(--rust,#b8440a)}',
      '.hs-addon:has(input:checked){border-color:var(--gold,#c8922a);background:rgba(200,146,42,.07)}',
      '.hs-breakdown{background:var(--warm,#f5ead6);border-radius:4px;padding:10px 14px;margin-bottom:1rem}',
      '.hs-bd-row{display:flex;justify-content:space-between;font-size:11.5px;color:var(--charcoal,#2c2418);padding:2px 0}',
      '.hs-bd-row:last-child{font-weight:700;border-top:1px solid rgba(200,146,42,.2);padding-top:5px;margin-top:3px}',
      '@media(max-width:600px){.hs-addon-grid{grid-template-columns:1fr}}',
    ].join('');
    document.head.appendChild(style);
  }

  // ── Bind events ───────────────────────────────────────────────────────────
  function bindEvents(container) {
    // Option buttons
    container.addEventListener('click', function (e) {
      var btn = e.target.closest('.hs-opt-btn');
      if (!btn) return;
      var catKey = btn.getAttribute('data-cat');
      var val    = btn.getAttribute('data-val');
      if (catKey && val) selectOption(catKey, val, btn);
    });

    // Add-on checkboxes
    container.addEventListener('change', function (e) {
      if (!e.target.classList.contains('hs-addon-cb')) return;
      var addonId = parseInt(e.target.getAttribute('data-addon-id'), 10);
      if (addonId) toggleAddon(addonId, e.target.checked);
      // Visual state for browsers without :has()
      var addon = e.target.closest('.hs-addon');
      if (addon) {
        addon.style.borderColor = e.target.checked ? 'var(--gold,#c8922a)' : '';
        addon.style.background  = e.target.checked ? 'rgba(200,146,42,.07)' : '';
      }
    });
  }

  // ── ATC intercept ─────────────────────────────────────────────────────────
  function interceptATC() {
    var form = document.querySelector('form[action="/cart/add"]');
    if (!form || form._hs_hooked) return;
    form._hs_hooked = true;

    form.addEventListener('submit', function () {
      injectHiddenProperties();
    });

    // Also hook the ATC button directly (some themes use JS submit)
    var atcBtn = form.querySelector('#pdp-atc-btn');
    if (atcBtn) {
      var origClick = atcBtn.onclick;
      atcBtn.addEventListener('click', function () {
        injectHiddenProperties();
      });
    }
  }

  // ── Main inject ───────────────────────────────────────────────────────────
  function inject(config) {
    pricingConfig = config;

    // Get the base variant price from the page
    var priceEl = document.getElementById('pdp-display-price');
    if (priceEl) {
      var priceText = priceEl.textContent.replace(/[^0-9.]/g, '');
      baseVariantPrice = parseFloat(priceText) || config.base_price || 49;
    }

    // Find injection point: before .pdp-atc-row
    var atcRow = document.querySelector('.pdp-atc-row');
    if (!atcRow) return;

    injectCSS();

    var wrap = document.createElement('div');
    wrap.id = 'hs-artwork-options';
    wrap.innerHTML = renderCategories(config.categories)
      + renderAddons(config.addons)
      + renderPriceBreakdown();

    atcRow.parentNode.insertBefore(wrap, atcRow);
    bindEvents(wrap);
    interceptATC();
    updatePriceDisplay();
  }

  // ── Load config ──────────────────────────────────────────────────────────
  function loadConfig() {
    var cached = getCached();
    if (cached) { inject(cached); return; }

    fetch(APP_URL + '/api/v1/pricing-config')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        setCache(data);
        inject(data);
      })
      .catch(function (err) {
        console.error('[HS Pricing] Failed to load config:', err);
      });
  }

  // ── Utility ───────────────────────────────────────────────────────────────
  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ── Boot ──────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadConfig);
  } else {
    loadConfig();
  }

})();
